import { Preferences } from '@capacitor/preferences'
import axios from 'axios'

import { isNative } from './platform'

/**
 * The retired device's half of a data transfer.
 *
 * Kept apart from `lib/transfer.ts` — and talking to the backend through a bare
 * axios call rather than the shared `api` instance — because `lib/api.ts`'s 401
 * interceptor calls into here. Routing that through `api` would be a circular
 * import, and worse, a 401 handler that can itself 401.
 *
 * What this exists to prevent: `api.ts` answers a 401 on native by discarding
 * the patient token, so the next launch re-bootstraps. For a device whose
 * account was just transferred away that is the wrong story twice over — the
 * user is told nothing and left wondering where their exercises went, and the
 * fresh patient comes with a fresh 30-day trial, which makes a transfer a trial
 * faucet. So before the token is thrown away we ask the server whether this
 * token was revoked by a transfer, and if it was, remember that locally.
 */

const RETIRED_KEY = 'lingotalk.transfer.retired'

/**
 * Notified the moment we discover this device's account was transferred away.
 *
 * The router's retirement guard only runs on a navigation, so without this the
 * discovery sits idle until the patient happens to navigate: they tap a button,
 * get an empty screen, and only land on the retired screen on the tap after
 * that. One subscriber (App.vue, which has the router) turns the discovery into
 * a navigation straight away.
 */
type RetirementListener = () => void

let retirementListener: RetirementListener | null = null

export function onRetirementDetected(listener: RetirementListener | null): void {
  retirementListener = listener
}

export interface RetiredState {
  /** ISO timestamp of the moment the account moved to another device. */
  transferred_at: string
}

/**
 * Ask whether this (now dead) token was revoked by a transfer.
 *
 * Sent in the body rather than as a bearer header: it no longer authenticates
 * anything, and the endpoint is deliberately unauthenticated — a device in this
 * state has no credential left to present. Best-effort; a network failure here
 * must never stand between the user and a working app, so it resolves null and
 * the ordinary re-bootstrap proceeds.
 */
export async function lookupTransferTombstone(token: string): Promise<string | null> {
  try {
    const { data } = await axios.post<{ transferred_at: string }>(
      `${import.meta.env.VITE_API_BASE_URL || '/api'}/patient/transfer/tombstone`,
      { token },
      { headers: { Accept: 'application/json' } },
    )
    return typeof data?.transferred_at === 'string' ? data.transferred_at : null
  } catch {
    // 404 is the common case (an ordinary revoked token), and anything else is
    // a question we simply cannot answer right now.
    return null
  }
}

export async function markRetired(transferredAt: string): Promise<void> {
  if (!isNative) return
  const state: RetiredState = { transferred_at: transferredAt }
  await Preferences.set({ key: RETIRED_KEY, value: JSON.stringify(state) })
}

export async function readRetired(): Promise<RetiredState | null> {
  if (!isNative) return null
  try {
    const { value } = await Preferences.get({ key: RETIRED_KEY })
    if (!value) return null
    const parsed = JSON.parse(value) as Partial<RetiredState>
    return typeof parsed.transferred_at === 'string'
      ? { transferred_at: parsed.transferred_at }
      : null
  } catch {
    return null
  }
}

export async function clearRetired(): Promise<void> {
  if (!isNative) return
  await Preferences.remove({ key: RETIRED_KEY })
}

/**
 * Called from the 401 path with the token that just stopped working. Returns
 * true when the 401 is explained by a transfer, which is the caller's cue to
 * route to the retired screen instead of silently re-bootstrapping.
 */
export async function recordRetirementIfTransferred(token: string): Promise<boolean> {
  const transferredAt = await lookupTransferTombstone(token)
  if (transferredAt === null) return false
  await markRetired(transferredAt)
  // Several requests can 401 together on the way down; the subscriber is
  // expected to be idempotent about where it navigates.
  retirementListener?.()
  return true
}
